(() => {
  if (customElements.get('shoppable-video-widget')) return;

  const SELECTOR_FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'video[controls]',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  class ShoppableVideoWidget extends HTMLElement {
    constructor() {
      super();
      this.activeIndex = 0;
      this.activeMedia = null;
      this.lastTrigger = null;
      this.touchStartY = null;
      this.wasPlayingBeforeHidden = false;
      this.addToCartInView = false;
      this.autoMinimizedForAddToCart = false;
      this.teaserManuallyMinimized = false;
      this.teaserReady = false;
      this.addToCartObserver = null;
      this.boundVisibilityChange = this.handleVisibilityChange.bind(this);
      this.boundKeydown = this.handleKeydown.bind(this);
    }

    connectedCallback() {
      try {
        this.items = JSON.parse(this.querySelector('[data-sv-items]').textContent).filter((item) => {
          return item && item.id && item.posterUrl && (
            (item.type === 'hosted' && Array.isArray(item.sources) && item.sources.length) ||
            (item.type === 'external' && item.externalId)
          );
        });
      } catch (error) {
        this.items = [];
      }

      this.cacheElements();
      this.removeNativeGalleryVideos();

      if (!this.items.length) {
        this.hidden = true;
        return;
      }

      this.loop = this.dataset.loop === 'true';
      this.autoAdvance = this.dataset.autoAdvance === 'true';
      this.allowDismiss = this.dataset.allowDismiss === 'true';
      this.muted = this.getStorage(localStorage, 'shoppableVideoMuted') !== 'false';
      this.bindEvents();
      this.observeAddToCartOnMobile();
      this.observeStickyCart();
      this.preloadTeaser().then((isReady) => {
        if (!this.isConnected) return;
        if (!isReady) {
          this.hidden = true;
          return;
        }
        this.teaserReady = true;
        this.showInitialState();
      });
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.boundKeydown);
      document.removeEventListener('visibilitychange', this.boundVisibilityChange);
      this.addToCartObserver?.disconnect();
      this.stickyCartObserver?.disconnect();
      this.releaseMedia();
    }

    cacheElements() {
      this.teaser = this.querySelector('[data-sv-teaser]');
      this.launcher = this.querySelector('[data-sv-launcher]');
      this.modal = this.querySelector('[data-sv-modal]');
      this.stage = this.querySelector('[data-sv-stage]');
      this.mediaContainer = this.querySelector('[data-sv-media]');
      this.poster = this.querySelector('[data-sv-poster]');
      this.loader = this.querySelector('[data-sv-loader]');
      this.error = this.querySelector('[data-sv-error]');
      this.progress = this.querySelector('[data-sv-progress]');
      this.soundButton = this.querySelector('[data-sv-sound]');
      this.previousButton = this.querySelector('[data-sv-previous]');
      this.nextButton = this.querySelector('[data-sv-next]');
      this.nextPreview = this.querySelector('[data-sv-next-preview]');
      this.nextPoster = this.querySelector('[data-sv-next-poster]');
      this.productCard = this.querySelector('[data-sv-product]');
      this.announcement = this.querySelector('[data-sv-announcement]');
    }

    removeNativeGalleryVideos() {
      document.querySelectorAll('.productView-nav[data-image-gallery-main]').forEach((gallery) => {
        const videoMediaIds = new Set();

        gallery.querySelectorAll('.productView-image').forEach((slide) => {
          const videoMedia = slide.querySelector('.media.has-video, .productView-video, .productView-external-video');
          if (!videoMedia) return;

          const mediaId = slide.querySelector('[data-media-id]')?.dataset.mediaId;
          if (mediaId) videoMediaIds.add(mediaId);
          (slide.closest('.slick-slide') || slide).remove();
        });

        const remainingSlides = gallery.querySelectorAll('.productView-image').length;
        gallery.dataset.mediaCount = String(remainingSlides);

        const productView = gallery.closest('.productView');
        productView?.querySelectorAll('.counter-total').forEach((counter) => {
          counter.textContent = String(remainingSlides);
        });

        videoMediaIds.forEach((mediaId) => {
          document.querySelectorAll(`[data-media-id="${mediaId}"]`).forEach((mediaItem) => {
            const thumbnail = mediaItem.closest('.productView-thumbnail');
            if (thumbnail) thumbnail.remove();
          });
        });
      });
    }

    preloadTeaser() {
      const item = this.items.find((videoItem) => videoItem.type === 'hosted');
      const source = item?.sources.find((videoSource) => videoSource.mime_type === 'video/mp4') || item?.sources[0];
      if (!source?.url) return Promise.resolve(false);

      return new Promise((resolve) => {
        const probe = document.createElement('video');
        const timeout = window.setTimeout(() => finish(false), 3000);
        let finished = false;

        const finish = (isReady) => {
          if (finished) return;
          finished = true;
          window.clearTimeout(timeout);
          probe.removeAttribute('src');
          probe.load();
          resolve(isReady);
        };

        probe.muted = true;
        probe.playsInline = true;
        probe.preload = 'auto';
        probe.addEventListener('canplay', () => finish(true), { once: true });
        probe.addEventListener('error', () => finish(false), { once: true });
        probe.src = source.url;
        probe.load();
      });
    }

    observeAddToCartOnMobile() {
      if (!window.matchMedia('(max-width: 749px)').matches || !('IntersectionObserver' in window)) return;

      const addToCart = document.querySelector('#product-add-to-cart');
      if (!addToCart) return;

      this.addToCartObserver = new IntersectionObserver((entries) => {
        const isInView = entries.some((entry) => entry.isIntersecting);
        this.addToCartInView = isInView;

        if (!isInView) {
          this.restoreTeaserAfterAddToCart();
          return;
        }

        if (this.teaserManuallyMinimized || (this.allowDismiss && this.getStorage(sessionStorage, 'shoppableVideoDismissed') === 'true')) return;
        this.autoMinimizedForAddToCart = true;
        this.minimize({ automatic: true });
      }, { threshold: 0.35 });

      this.addToCartObserver.observe(addToCart);
    }

    observeStickyCart() {
      if (!window.matchMedia('(max-width: 749px)').matches) return;

      const stickyCart = document.querySelector('[data-sticky-add-to-cart]');
      if (!stickyCart) return;

      this.originalBottomOffset = parseInt(this.style.getPropertyValue('--sv-bottom-offset')) || 0;

      const isStickyVisible = () =>
        stickyCart.classList.contains('show-sticky') ||
        (stickyCart.classList.contains('always-on-mobile') && !stickyCart.classList.contains('hidden-sticky'));

      const updateOffset = () => {
        if (isStickyVisible()) {
          this.style.setProperty('--sv-bottom-offset', `${stickyCart.offsetHeight + this.originalBottomOffset}px`);
          document.body.classList.add('sticky-buy-active');
        } else {
          this.style.setProperty('--sv-bottom-offset', `${this.originalBottomOffset}px`);
          document.body.classList.remove('sticky-buy-active');
        }
      };

      this.stickyCartObserver = new MutationObserver(updateOffset);
      this.stickyCartObserver.observe(stickyCart, { attributes: true, attributeFilter: ['class'] });
      updateOffset();
    }

    bindEvents() {
      this.addEventListener('click', (event) => {
        const openButton = event.target.closest('[data-sv-open]');
        if (openButton) this.open(openButton);
        if (event.target.closest('[data-sv-minimize]')) this.minimize();
        if (event.target.closest('[data-sv-dismiss]')) this.dismiss();
        if (event.target.closest('[data-sv-close]')) this.close();
        if (event.target.closest('[data-sv-sound]')) this.toggleSound();
        if (event.target.closest('[data-sv-next]')) this.move(1);
        if (event.target.closest('[data-sv-previous]')) this.move(-1);
        if (event.target.closest('[data-sv-retry]')) this.renderActiveItem();
        if (event.target.closest('[data-sv-like]')) this.toggleLike(event.target.closest('[data-sv-like]'));
        if (event.target.closest('[data-sv-share]')) this.share();
        if (event.target.closest('[data-sv-product-shortcut]')) {
          this.productCard.focus({ preventScroll: true });
          this.productCard.scrollIntoView({ behavior: this.reducedMotion() ? 'auto' : 'smooth', block: 'nearest' });
        }
      });

      this.stage.addEventListener('touchstart', (event) => {
        this.touchStartY = event.changedTouches[0].clientY;
      }, { passive: true });

      this.mediaContainer.addEventListener('click', (event) => {
        if (event.target !== this.activeMedia || this.activeMedia.tagName !== 'VIDEO') return;
        if (this.activeMedia.paused) {
          this.activeMedia.play().catch(() => this.showPlaybackError());
          this.announce('Video playing');
        } else {
          this.activeMedia.pause();
          this.announce('Video paused');
        }
      });

      this.stage.addEventListener('touchend', (event) => {
        if (this.touchStartY === null) return;
        const distance = event.changedTouches[0].clientY - this.touchStartY;
        this.touchStartY = null;
        if (Math.abs(distance) < 55) return;
        this.move(distance < 0 ? 1 : -1);
      }, { passive: true });
    }

    showInitialState() {
      const dismissed = this.allowDismiss && this.getStorage(sessionStorage, 'shoppableVideoDismissed') === 'true';
      if (dismissed || this.dataset.initialMode === 'hidden') return;

      if (this.addToCartInView) {
        this.launcher.hidden = false;
        return;
      }

      if (this.dataset.initialMode === 'teaser') {
        this.teaser.hidden = false;
      } else {
        this.launcher.hidden = false;
      }
    }

    open(trigger) {
      this.lastTrigger = trigger;
      this.teaser.hidden = true;
      this.launcher.hidden = true;
      this.modal.hidden = false;
      document.body.classList.add('shoppable-video-open');
      document.addEventListener('keydown', this.boundKeydown);
      document.addEventListener('visibilitychange', this.boundVisibilityChange);
      this.renderActiveItem();
      requestAnimationFrame(() => this.querySelector('[data-sv-close]:not(.shoppable-video__backdrop)').focus());
    }

    close() {
      if (this.modal.hidden) return;
      this.modal.hidden = true;
      document.body.classList.remove('shoppable-video-open');
      document.removeEventListener('keydown', this.boundKeydown);
      document.removeEventListener('visibilitychange', this.boundVisibilityChange);
      this.releaseMedia();
      if (this.getStorage(sessionStorage, 'shoppableVideoDismissed') !== 'true') {
        this.launcher.hidden = false;
      }
      const returnTarget = this.lastTrigger && !this.lastTrigger.closest('[hidden]')
        ? this.lastTrigger
        : this.querySelector('[data-sv-open="minimized_launcher"]');
      if (returnTarget && document.contains(returnTarget)) returnTarget.focus();
    }

    minimize({ automatic = false } = {}) {
      if (!automatic) {
        this.teaserManuallyMinimized = true;
        this.autoMinimizedForAddToCart = false;
      }
      this.querySelector('.shoppable-video__teaser-preview')?.pause();
      this.teaser.hidden = true;
      this.launcher.hidden = false;
    }

    restoreTeaserAfterAddToCart() {
      if (!this.teaserReady || !this.autoMinimizedForAddToCart || this.teaserManuallyMinimized || !this.modal.hidden || this.dataset.initialMode !== 'teaser') return;
      this.autoMinimizedForAddToCart = false;
      this.launcher.hidden = true;
      this.teaser.hidden = false;
      this.querySelector('.shoppable-video__teaser-preview')?.play().catch(() => {});
    }

    dismiss() {
      this.setStorage(sessionStorage, 'shoppableVideoDismissed', 'true');
      this.teaser.hidden = true;
      this.launcher.hidden = true;
    }

    renderActiveItem() {
      this.releaseMedia();
      const item = this.items[this.activeIndex];
      this.poster.src = item.posterUrl;
      this.poster.hidden = false;
      this.loader.hidden = false;
      this.error.hidden = true;
      this.progress.style.width = '0';
      this.updateNavigation();
      this.updateSoundButton();

      if (item.type === 'external') {
        this.renderExternalVideo(item);
      } else {
        this.renderHostedVideo(item);
      }
    }

    renderHostedVideo(item) {
      const preferredSource = item.sources.find((source) => source.mime_type === 'video/mp4') || item.sources[0];
      if (!preferredSource || !preferredSource.url) {
        this.showPlaybackError();
        return;
      }

      const video = document.createElement('video');
      video.playsInline = true;
      video.autoplay = true;
      video.preload = 'metadata';
      video.muted = this.muted;
      video.defaultMuted = this.muted;
      video.poster = item.posterUrl;
      video.setAttribute('playsinline', '');
      video.setAttribute('autoplay', '');
      if (this.muted) video.setAttribute('muted', '');
      video.setAttribute('aria-label', item.title || 'Product video');
      this.activeMedia = video;
      this.mediaContainer.insertBefore(video, this.poster);

      const startPlayback = () => {
        if (this.activeMedia !== video) return;
        video.play().catch(() => {
          if (this.activeMedia !== video || video.muted) return;
          video.muted = true;
          video.defaultMuted = true;
          video.setAttribute('muted', '');
          this.muted = true;
          this.updateSoundButton();
          video.play().catch(() => {});
        });
      };

      video.addEventListener('loadeddata', startPlayback, { once: true });
      video.addEventListener('canplay', startPlayback, { once: true });
      video.addEventListener('waiting', () => { this.loader.hidden = false; });
      video.addEventListener('playing', () => {
        this.loader.hidden = true;
        this.poster.hidden = true;
      });
      video.addEventListener('timeupdate', () => {
        if (Number.isFinite(video.duration) && video.duration > 0) {
          this.progress.style.width = `${(video.currentTime / video.duration) * 100}%`;
        }
      });
      video.addEventListener('ended', () => {
        this.progress.style.width = '100%';
        if (this.autoAdvance) this.move(1);
      });
      video.addEventListener('error', () => this.showPlaybackError());
      video.src = preferredSource.url;
      video.load();
      startPlayback();
    }

    renderExternalVideo(item) {
      const iframe = document.createElement('iframe');
      const mutedValue = this.muted ? '1' : '0';
      if (item.host === 'youtube') {
        iframe.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(item.externalId)}?autoplay=1&mute=${mutedValue}&playsinline=1&enablejsapi=1`;
      } else if (item.host === 'vimeo') {
        iframe.src = `https://player.vimeo.com/video/${encodeURIComponent(item.externalId)}?autoplay=1&muted=${mutedValue}&playsinline=1`;
      } else {
        this.showPlaybackError();
        return;
      }
      iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
      iframe.allowFullscreen = true;
      iframe.title = item.title || 'Product video';
      iframe.addEventListener('load', () => {
        this.loader.hidden = true;
        this.poster.hidden = true;
      }, { once: true });
      iframe.addEventListener('error', () => this.showPlaybackError());
      this.activeMedia = iframe;
      this.mediaContainer.insertBefore(iframe, this.poster);
    }

    releaseMedia() {
      if (!this.activeMedia) return;
      if (this.activeMedia.tagName === 'VIDEO') {
        this.activeMedia.pause();
        this.activeMedia.removeAttribute('src');
        this.activeMedia.load();
      } else {
        this.activeMedia.src = 'about:blank';
      }
      this.activeMedia.remove();
      this.activeMedia = null;
    }

    showPlaybackError() {
      this.loader.hidden = true;
      this.error.hidden = false;
      this.poster.hidden = false;
      this.announce('Video unavailable. You can retry or choose another video.');
    }

    move(direction) {
      if (this.items.length < 2) return;
      let nextIndex = this.activeIndex + direction;
      if (this.loop) {
        nextIndex = (nextIndex + this.items.length) % this.items.length;
      } else if (nextIndex < 0 || nextIndex >= this.items.length) {
        return;
      }
      this.activeIndex = nextIndex;
      this.renderActiveItem();
      this.announce(`Video ${this.activeIndex + 1} of ${this.items.length}`);
    }

    updateNavigation() {
      const hasMultipleItems = this.items.length > 1;
      this.previousButton.hidden = !hasMultipleItems;
      this.nextButton.hidden = !hasMultipleItems;
      this.previousButton.disabled = !this.loop && this.activeIndex === 0;
      this.nextButton.disabled = !this.loop && this.activeIndex === this.items.length - 1;

      let nextIndex = this.activeIndex + 1;
      if (nextIndex >= this.items.length && this.loop) nextIndex = 0;
      const nextItem = this.items[nextIndex];
      this.nextPreview.hidden = !nextItem;
      if (nextItem) this.nextPoster.src = nextItem.posterUrl;
    }

    toggleSound() {
      this.muted = !this.muted;
      this.setStorage(localStorage, 'shoppableVideoMuted', String(this.muted));

      if (this.activeMedia && this.activeMedia.tagName === 'VIDEO') {
        this.activeMedia.muted = this.muted;
      } else if (this.activeMedia) {
        const item = this.items[this.activeIndex];
        if (item.host === 'youtube') {
          this.activeMedia.contentWindow.postMessage(JSON.stringify({
            event: 'command',
            func: this.muted ? 'mute' : 'unMute',
            args: []
          }), '*');
        } else if (item.host === 'vimeo') {
          this.activeMedia.contentWindow.postMessage({
            method: 'setVolume',
            value: this.muted ? 0 : 1
          }, '*');
        }
      }
      this.updateSoundButton();
    }

    updateSoundButton() {
      this.soundButton.classList.toggle('is-muted', this.muted);
      this.soundButton.setAttribute('aria-label', this.muted ? 'Unmute video' : 'Mute video');
    }

    toggleLike(button) {
      const liked = button.getAttribute('aria-pressed') !== 'true';
      button.setAttribute('aria-pressed', String(liked));
      button.setAttribute('aria-label', liked ? 'Unlike video' : 'Like video');
      button.querySelector('[data-sv-like-label]').textContent = liked ? 'Liked' : 'Like';
    }

    async share() {
      const item = this.items[this.activeIndex];
      const shareData = {
        title: item.title || document.title,
        url: item.shareUrl || window.location.href
      };

      try {
        if (navigator.share) {
          await navigator.share(shareData);
          return;
        }
        await this.copyText(shareData.url);
        const label = this.querySelector('[data-sv-share-label]');
        label.textContent = 'Copied';
        this.announce('Video link copied');
        window.setTimeout(() => { label.textContent = 'Share'; }, 1800);
      } catch (error) {
        if (error && error.name !== 'AbortError') this.announce('Unable to share this video');
      }
    }

    async copyText(value) {
      if (navigator.clipboard && window.isSecureContext) {
        return navigator.clipboard.writeText(value);
      }
      const input = document.createElement('textarea');
      input.value = value;
      input.setAttribute('readonly', '');
      input.style.position = 'fixed';
      input.style.opacity = '0';
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      input.remove();
    }

    handleVisibilityChange() {
      if (!this.activeMedia || this.activeMedia.tagName !== 'VIDEO') return;
      if (document.hidden) {
        this.wasPlayingBeforeHidden = !this.activeMedia.paused;
        this.activeMedia.pause();
      } else if (this.wasPlayingBeforeHidden && !this.modal.hidden) {
        this.activeMedia.play().catch(() => {});
      }
    }

    handleKeydown(event) {
      if (this.modal.hidden) return;
      const tagName = event.target.tagName;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      } else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        event.preventDefault();
        this.move(1);
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        event.preventDefault();
        this.move(-1);
      } else if (event.key.toLowerCase() === 'm') {
        event.preventDefault();
        this.toggleSound();
      } else if (event.key === ' ' && tagName !== 'BUTTON' && tagName !== 'A') {
        event.preventDefault();
        if (this.activeMedia && this.activeMedia.tagName === 'VIDEO') {
          if (this.activeMedia.paused) this.activeMedia.play().catch(() => {});
          else this.activeMedia.pause();
        }
      } else if (event.key === 'Tab') {
        this.trapFocus(event);
      }
    }

    trapFocus(event) {
      const focusable = Array.from(this.modal.querySelectorAll(SELECTOR_FOCUSABLE)).filter((element) => {
        return !element.hidden && element.offsetParent !== null;
      });
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    announce(message) {
      this.announcement.textContent = '';
      requestAnimationFrame(() => { this.announcement.textContent = message; });
    }

    reducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    getStorage(storage, key) {
      try {
        return storage.getItem(key);
      } catch (error) {
        return null;
      }
    }

    setStorage(storage, key, value) {
      try {
        storage.setItem(key, value);
      } catch (error) {
        // Storage may be unavailable in privacy-restricted browsing contexts.
      }
    }
  }

  customElements.define('shoppable-video-widget', ShoppableVideoWidget);
})();
